// ============================================================================
// RISK PROFILE FORM - Model & Interfaces
// ============================================================================

// ========== ENUMS ==========

export enum LegalStructure {
  SPA = 'SPA',
  SDP = 'SDP',
}

export enum CaValue {
  LESS_THAN_250K = 'LESS_THAN_250K',
  BETWEEN_250K_AND_500K = 'BETWEEN_250K_AND_500K',
  BETWEEN_500K_AND_1000K = 'BETWEEN_500K_AND_1000K',
  BETWEEN_1000K_AND_2500K = 'BETWEEN_1000K_AND_2500K',
  GREATER_THAN_2500K = 'GREATER_THAN_2500K',
}

export enum DateDeCreation {
  LESS_THAN_THREE_YEARS = 'LESS_THAN_THREE_YEARS',
  MORE_THAN_THREE_YEARS = 'MORE_THAN_THREE_YEARS',
}

export enum YesNo {
  NO = 'NO',
  YES = 'YES',
}

export enum MaritalStatus {
  SINGLE = 'SINGLE',
  IN_A_RELATIONSHIP = 'IN_A_RELATIONSHIP',
}

export enum OccupancyStatus {
  OWNER = 'OWNER',
  TENANT = 'TENANT',
}

export enum Income {
  LESS_THAN_35K = 'LESS_THAN_35K',
  BETWEEN_35K_AND_60K = 'BETWEEN_35K_AND_60K',
  BETWEEN_60K_AND_100K = 'BETWEEN_60K_AND_100K',
  BETWEEN_100K_AND_250K = 'BETWEEN_100K_AND_250K',
  GREATER_THAN_250K = 'GREATER_THAN_250K',
}

export enum PatrimonyValuePersonMoral {
  LOWER_THAN_100K = 'LOWER_THAN_100K',
  BETWEEN_100K_AND_500K = 'BETWEEN_100K_AND_500K',
  BETWEEN_500K_AND_1500K = 'BETWEEN_500K_AND_1500K',
  BETWEEN_1500K_AND_3000K = 'BETWEEN_1500K_AND_3000K',
  GREATER_THAN_3000K = 'GREATER_THAN_3000K',
}

export enum PatrimonyValuePersonPhysique {
  LOWER_THAN_100K = 'LOWER_THAN_100K',
  BETWEEN_100K_AND_300K = 'BETWEEN_100K_AND_300K',
  BETWEEN_300K_AND_500K = 'BETWEEN_300K_AND_500K',
  BETWEEN_500K_AND_1M = 'BETWEEN_500K_AND_1M',
  GREATER_THAN_1M = 'GREATER_THAN_1M',
}

export enum RiskTolerance {
  LOW_RISK_TOLERANCE = 'LOW_RISK_TOLERANCE',
  MEDIUM_RISK_TOLERANCE = 'MEDIUM_RISK_TOLERANCE',
  HIGH_RISK_TOLERANCE = 'HIGH_RISK_TOLERANCE',
}

export enum DesiredYield {
  LOW_YIELD_NO_RISK = 'LOW_YIELD_NO_RISK',
  LOW_YIELD_LOW_RISK = 'LOW_YIELD_LOW_RISK',
  MEDIUM_YIELD_RISK = 'MEDIUM_YIELD_RISK',
  HIGH_YIELD_HIGH_RISK = 'HIGH_YIELD_HIGH_RISK',
}

export enum ConceptReturnRisk {
  PROPORTIONAL = 'PROPORTIONAL',
  INVERSELY_PROPORTIONAL = 'INVERSELY_PROPORTIONAL',
  UNKNOWN = 'UNKNOWN',
}

export enum LossTolerance {
  NONE = 'NONE',
  LIMITED = 'LIMITED',
  PARTIAL_LOSS = 'PARTIAL_LOSS',
  TOTAL_LOSS = 'TOTAL_LOSS',
  LOSS_BEYOND_CAPITAL = 'LOSS_BEYOND_CAPITAL',
}

export enum SavingsRate {
  GREATER_THAN_102 = 'GREATER_THAN_102',
  EQUAL_102 = 'EQUAL_102',
  UNKNOWN = 'UNKNOWN',
}

export enum InvestmentDecreaseReaction {
  INVEST = 'INVEST',
  SELL_TOTALLY = 'SELL_TOTALLY',
  SELL_PARTIALLY = 'SELL_PARTIALLY',
  NONE = 'NONE',
}

export enum MoneyGames {
  NEVER = 'NEVER',
  OCCASIONALLY = 'OCCASIONALLY',
  FREQUENTLY = 'FREQUENTLY',
}

export enum InvestmentKnowledgeType {
  KNOW_MONETARY_PRODUCT = 'knowMonetaryProduct',
  KNOW_BOND = 'knowBond',
  KNOW_STOCK = 'knowStock',
  KNOW_SCPI = 'knowScpi',
  KNOW_PRIVATE_EQUITY = 'knowPrivateEquity',
  KNOW_LIFE_INSURANCE = 'knowLifeInsurance',
  KNOW_DERIVATIVE_PRODUCTS = 'knowDerivativeProducts',
}

export enum TrueFalseUnknown {
  YES = 'YES',
  NO = 'NO',
  UNKNOWN = 'UNKNOWN',
}

export enum ManagementMode {
  YOURSELF = 'YOURSELF',
  ADVISOR = 'ADVISOR',
  UNDER_MANDATE = 'UNDER_MANDATE',
}

// ========== MAIN INTERFACE ==========

export interface RiskProfileForm {
  // ========== PERSONNE MORALE UNIQUEMENT ==========
  legalStructure?: LegalStructure;
  caValue?: CaValue;
  dateDeCreation?: DateDeCreation;
  resultatNet?: YesNo;
  leasing?: YesNo;
  tresorerieAPlacer?: YesNo;

  // ========== PERSONNE PHYSIQUE UNIQUEMENT ==========
  maritalStatus?: MaritalStatus;
  occupancyStatus?: OccupancyStatus;
  income?: Income;
  workInFinance?: YesNo;
  credit?: YesNo;
  readSpecializedPress?: YesNo;

  // ========== COMMUN ==========
  patrimonyValue: string;
  riskTolerance: RiskTolerance;
  desiredYield: DesiredYield;
  conceptReturnRisk: ConceptReturnRisk;
  lossTolerance: LossTolerance;
  savingsRate: SavingsRate;
  hadInvestmentDecrease: YesNo;
  investmentDecreaseReaction: InvestmentDecreaseReaction;
  moneyGames: MoneyGames;

  // ========== CONNAISSANCE INVESTISSEMENT ==========
  investmentKnowledge: string[];

  // Sous-champs conditionnels - Fonds monétaires
  monetaryProduct?: TrueFalseUnknown;
  investMonetaryProduct?: boolean;
  monetaryProductManagementMode?: ManagementMode;

  // Sous-champs conditionnels - Obligations
  bond?: TrueFalseUnknown;
  investBond?: boolean;
  bondManagementMode?: ManagementMode;

  // Sous-champs conditionnels - Actions
  stock?: TrueFalseUnknown;
  investStock?: boolean;
  stockManagementMode?: ManagementMode;

  // Sous-champs conditionnels - SCPI
  scpi?: TrueFalseUnknown;
  investScpi?: boolean;
  scpiManagementMode?: ManagementMode;

  // Sous-champs conditionnels - Capital investissement
  privateEquity?: TrueFalseUnknown;
  investPrivateEquity?: boolean;
  privateEquityManagementMode?: ManagementMode;

  // Sous-champs conditionnels - Assurance Vie / PER
  lifeInsurance?: TrueFalseUnknown;
  investLifeInsurance?: boolean;
  lifeInsuranceManagementMode?: ManagementMode;

  // Sous-champs conditionnels - Produits dérivés
  derivativeProducts?: TrueFalseUnknown;
  investDerivativeProducts?: boolean;
  derivativeProductsManagementMode?: ManagementMode;
}

// ========== OPTIONS CONFIGURATION ==========

export interface SelectOption {
  value: string;
  label: string;
}

export interface RadioOption {
  value: string | boolean;
  label: string;
  icon?: string;
  prefix?: {
    title: string;
    color?: string;
    backgroundColor?: string;
  };
}

// ========== OPTIONS LISTS ==========

export const LEGAL_STRUCTURE_OPTIONS: SelectOption[] = [
  { value: LegalStructure.SPA, label: 'Société par actions' },
  { value: LegalStructure.SDP, label: 'Société de personnes' },
];

export const CA_VALUE_OPTIONS: SelectOption[] = [
  { value: CaValue.LESS_THAN_250K, label: 'Inférieur à 250k€' },
  { value: CaValue.BETWEEN_250K_AND_500K, label: 'Entre 250k et 500k€' },
  { value: CaValue.BETWEEN_500K_AND_1000K, label: 'Entre 500k et 1M€' },
  { value: CaValue.BETWEEN_1000K_AND_2500K, label: 'Entre 1M€ et 2.5M€' },
  { value: CaValue.GREATER_THAN_2500K, label: 'Supérieur à 2.5M€' },
];

export const DATE_CREATION_OPTIONS: SelectOption[] = [
  { value: DateDeCreation.LESS_THAN_THREE_YEARS, label: '< 3 ans' },
  { value: DateDeCreation.MORE_THAN_THREE_YEARS, label: '> 3 ans' },
];

export const YES_NO_OPTIONS: RadioOption[] = [
  { value: YesNo.NO, label: 'Non' },
  { value: YesNo.YES, label: 'Oui' },
];

export const MARITAL_STATUS_OPTIONS: RadioOption[] = [
  { value: MaritalStatus.SINGLE, label: 'Célibataire' },
  { value: MaritalStatus.IN_A_RELATIONSHIP, label: 'En couple' },
];

export const OCCUPANCY_STATUS_OPTIONS: RadioOption[] = [
  { value: OccupancyStatus.OWNER, label: 'Propriétaire' },
  { value: OccupancyStatus.TENANT, label: 'Locataire' },
];

export const INCOME_OPTIONS: SelectOption[] = [
  { value: Income.LESS_THAN_35K, label: 'Inférieur à 35k€' },
  { value: Income.BETWEEN_35K_AND_60K, label: 'Entre 35k et 60k€' },
  { value: Income.BETWEEN_60K_AND_100K, label: 'Entre 60k et 100k€' },
  { value: Income.BETWEEN_100K_AND_250K, label: 'Entre 100k et 250k€' },
  { value: Income.GREATER_THAN_250K, label: 'Supérieur à 250k€' },
];

export const PATRIMONY_VALUE_PERSON_MORAL_OPTIONS: SelectOption[] = [
  {
    value: PatrimonyValuePersonMoral.LOWER_THAN_100K,
    label: 'Inférieur à 100k€',
  },
  {
    value: PatrimonyValuePersonMoral.BETWEEN_100K_AND_500K,
    label: 'Entre 100k et 500k€',
  },
  {
    value: PatrimonyValuePersonMoral.BETWEEN_500K_AND_1500K,
    label: 'Entre 500k et 1.5M€',
  },
  {
    value: PatrimonyValuePersonMoral.BETWEEN_1500K_AND_3000K,
    label: 'Entre 1.5M€ et 3M€',
  },
  {
    value: PatrimonyValuePersonMoral.GREATER_THAN_3000K,
    label: 'Supérieur à 3M€',
  },
];

export const PATRIMONY_VALUE_PERSON_PHYSIQUE_OPTIONS: SelectOption[] = [
  {
    value: PatrimonyValuePersonPhysique.LOWER_THAN_100K,
    label: 'Inférieur à 100k€',
  },
  {
    value: PatrimonyValuePersonPhysique.BETWEEN_100K_AND_300K,
    label: 'Entre 100k et 300k€',
  },
  {
    value: PatrimonyValuePersonPhysique.BETWEEN_300K_AND_500K,
    label: 'Entre 300k et 500k€',
  },
  {
    value: PatrimonyValuePersonPhysique.BETWEEN_500K_AND_1M,
    label: 'Entre 500k€ et 1M€',
  },
  {
    value: PatrimonyValuePersonPhysique.GREATER_THAN_1M,
    label: 'Supérieur à 1M€',
  },
];

export const RISK_TOLERANCE_OPTIONS: RadioOption[] = [
  {
    value: RiskTolerance.LOW_RISK_TOLERANCE,
    label:
      'Risque faible, tout en privilégiant la protection de votre capital sur la durée, vous acceptez une diversification partielle de vos investissements sur des actifs plus volatils et donc plus risqués.',
    prefix: { title: '●', color: '#c00000' },
  },
  {
    value: RiskTolerance.MEDIUM_RISK_TOLERANCE,
    label:
      'Risque moyen, en acceptant de diversifier significativement vos actifs sur des supports à forte volatilité pouvant entraîner une perte en capital, vous recherchez une valorisation importante de votre investissement.',
    prefix: { title: '●', color: '#00b050' },
  },
  {
    value: RiskTolerance.HIGH_RISK_TOLERANCE,
    label:
      "Risque élevé, en contrepartie d'une perte potentielle partielle, voire totale, de votre épargne, vous cherchez avant tout à maximiser la performance de votre investissement.",
    prefix: { title: '●', color: '#00b0f0' },
  },
];

export const DESIRED_YIELD_OPTIONS: RadioOption[] = [
  {
    value: DesiredYield.LOW_YIELD_NO_RISK,
    label: 'Un potentiel de rendement faible sans risque de perte en capital',
  },
  {
    value: DesiredYield.LOW_YIELD_LOW_RISK,
    label:
      'Un potentiel de rendement faible avec peu de risque de perte en capital',
  },
  {
    value: DesiredYield.MEDIUM_YIELD_RISK,
    label: 'Un potentiel de rendement moyen avec un risque de perte en capital',
  },
  {
    value: DesiredYield.HIGH_YIELD_HIGH_RISK,
    label:
      'Un potentiel de rendement élevé avec un risque élevé de perte en capital',
  },
];

export const CONCEPT_RETURN_RISK_OPTIONS: RadioOption[] = [
  {
    value: ConceptReturnRisk.PROPORTIONAL,
    label:
      'Si vous voulez augmenter votre rendement potentiel, vous devez accepter le risque de perte en capital.',
  },
  {
    value: ConceptReturnRisk.INVERSELY_PROPORTIONAL,
    label:
      'Si vous augmentez le risque, vous diminuez votre rendement potentiel.',
  },
  { value: ConceptReturnRisk.UNKNOWN, label: 'Je ne sais pas' },
];
export const LOSS_TOLERANCE_OPTIONS: RadioOption[] = [
  {
    value: LossTolerance.NONE,
    label: 'Aucune capacité à supporter des pertes',
  },
  {
    value: LossTolerance.LIMITED,
    label: 'Capacité limitée à supporter des pertes en capital',
  },
  { value: LossTolerance.PARTIAL_LOSS, label: 'Perte partielle possible' },
  { value: LossTolerance.TOTAL_LOSS, label: 'Perte totale possible' },
  {
    value: LossTolerance.LOSS_BEYOND_CAPITAL,
    label: "Perte au-delà de l'investissement initial possible",
  },
];

export const SAVINGS_RATE_OPTIONS: RadioOption[] = [
  { value: SavingsRate.GREATER_THAN_102, label: 'Plus de 102 €' },
  { value: SavingsRate.EQUAL_102, label: 'Exactement 102 €' },
  { value: SavingsRate.UNKNOWN, label: 'Je ne sais pas' },
];

export const INVESTMENT_DECREASE_REACTION_OPTIONS: RadioOption[] = [
  {
    value: InvestmentDecreaseReaction.INVEST,
    label:
      'Vous investissez de nouveau pour profiter des opportunités de marché',
  },
  {
    value: InvestmentDecreaseReaction.SELL_TOTALLY,
    label:
      'Vous vendez tout pour éventuellement réinvestir dans des supports moins risqués',
  },
  {
    value: InvestmentDecreaseReaction.SELL_PARTIALLY,
    label:
      'Vous vendez seulement une partie pour éventuellement réinvestir sur des supports moins risqués',
  },
  { value: InvestmentDecreaseReaction.NONE, label: 'Vous ne changez rien' },
];

export const MONEY_GAMES_OPTIONS: RadioOption[] = [
  { value: MoneyGames.NEVER, label: 'Jamais' },
  { value: MoneyGames.OCCASIONALLY, label: "À l'occasion" },
  { value: MoneyGames.FREQUENTLY, label: 'Fréquemment' },
];

export const INVESTMENT_KNOWLEDGE_OPTIONS: SelectOption[] = [
  {
    value: InvestmentKnowledgeType.KNOW_MONETARY_PRODUCT,
    label: 'Fonds monétaires, Livrets, Fonds Euro',
  },
  { value: InvestmentKnowledgeType.KNOW_BOND, label: 'Obligations' },
  {
    value: InvestmentKnowledgeType.KNOW_STOCK,
    label: 'Actions sur le marché boursier',
  },
  { value: InvestmentKnowledgeType.KNOW_SCPI, label: 'SCPI' },
  {
    value: InvestmentKnowledgeType.KNOW_PRIVATE_EQUITY,
    label: 'Capital investissement',
  },
  {
    value: InvestmentKnowledgeType.KNOW_LIFE_INSURANCE,
    label: 'Assurance Vie / PER',
  },
  {
    value: InvestmentKnowledgeType.KNOW_DERIVATIVE_PRODUCTS,
    label:
      'Produits financiers complexes, Instruments financiers dérivés, ETF, Produits structurés',
  },
];

export const TRUE_FALSE_UNKNOWN_OPTIONS: RadioOption[] = [
  { value: TrueFalseUnknown.YES, label: 'Vrai' },
  { value: TrueFalseUnknown.NO, label: 'Faux' },
  { value: TrueFalseUnknown.UNKNOWN, label: 'Je ne sais pas' },
];

export const MANAGEMENT_MODE_OPTIONS: SelectOption[] = [
  { value: ManagementMode.YOURSELF, label: 'Directe' },
  { value: ManagementMode.ADVISOR, label: 'Conseillée' },
  { value: ManagementMode.UNDER_MANDATE, label: 'Sous mandat' },
];

// ========== INVESTMENT KNOWLEDGE QUESTIONS ==========

export interface InvestmentKnowledgeQuestion {
  type: InvestmentKnowledgeType;
  title: string;
  question: string;
  knowledgeField: string;
  investField: string;
  managementModeField: string;
}

export const INVESTMENT_KNOWLEDGE_QUESTIONS: InvestmentKnowledgeQuestion[] = [
  {
    type: InvestmentKnowledgeType.KNOW_MONETARY_PRODUCT,
    title: 'Fonds monétaires, Livrets, Fonds Euro',
    question:
      'A moyen long terme, les produits monétaires offrent une espérance de rendement inférieure à celle de certains actifs risqués.',
    knowledgeField: 'monetaryProduct',
    investField: 'investMonetaryProduct',
    managementModeField: 'monetaryProductManagementMode',
  },
  {
    type: InvestmentKnowledgeType.KNOW_BOND,
    title: 'Obligations',
    question:
      "Si les taux d'intérêt augmentent, le prix des obligations évoluent à la baisse ?",
    knowledgeField: 'bond',
    investField: 'investBond',
    managementModeField: 'bondManagementMode',
  },
  {
    type: InvestmentKnowledgeType.KNOW_STOCK,
    title: 'Actions sur le marché boursier',
    question:
      "Détenir une action correspond à détenir une dette d'une entreprise ?",
    knowledgeField: 'stock',
    investField: 'investStock',
    managementModeField: 'stockManagementMode',
  },
  {
    type: InvestmentKnowledgeType.KNOW_SCPI,
    title: 'SCPI',
    question:
      "L'investissement en parts de SCPI offre une garantie en capital ?",
    knowledgeField: 'scpi',
    investField: 'investScpi',
    managementModeField: 'scpiManagementMode',
  },
  {
    type: InvestmentKnowledgeType.KNOW_PRIVATE_EQUITY,
    title: 'Capital investissement',
    question:
      'Les principales caractéristiques des parts de FCPI / FCPR sont une possibilité de forte rentabilité, un risque élevé et une faible liquidité ?',
    knowledgeField: 'privateEquity',
    investField: 'investPrivateEquity',
    managementModeField: 'privateEquityManagementMode',
  },
  {
    type: InvestmentKnowledgeType.KNOW_LIFE_INSURANCE,
    title: 'Assurance Vie / PER',
    question:
      "L'assurance vie comporte aucun risque de perte sur le long terme ?",
    knowledgeField: 'lifeInsurance',
    investField: 'investLifeInsurance',
    managementModeField: 'lifeInsuranceManagementMode',
  },
  {
    type: InvestmentKnowledgeType.KNOW_DERIVATIVE_PRODUCTS,
    title:
      'Produits financiers complexes, Instruments financiers dérivés, ETF, Produits structurés',
    question:
      'Un ETF est un panier de titres qui se négocient en bourse, comme une action ?',
    knowledgeField: 'derivativeProducts',
    investField: 'investDerivativeProducts',
    managementModeField: 'derivativeProductsManagementMode',
  },
];
